import React, { useState, useEffect } from "react";
import {
  EyeIcon,
  PencilIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  PowerIcon,
} from "@heroicons/react/24/outline";
import BotSettingsModal from "../components/modal/BotSettingsModal";
import CreateBotModal from "../components/modal/CreateBotModal";
import useBotStrategyStore from "../stores/bot";
import SuccessModal from "../components/modal/SuccessModal";

const BotStrategy = () => {
  const {
    strategies,
    isLoading,
    isSubmitting,
    botCounts,
    isCountLoading,
    botDetail,
    isDetailLoading,
    getBotStrategies,
    getBotCounts,
    getBotProfileDetail, // ✅ new
    updateBotStrategy,
    deleteBotProfile,
    toggleBotProfileStatus,
  } = useBotStrategyStore();

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    getBotStrategies();
    getBotCounts();
  }, []);

  const handleView = async (bot) => {
    setSelectedBot(bot);
    setIsViewMode(true);
    await getBotProfileDetail(bot.bot_master_id);
    setIsSettingsOpen(true);
  };

  const handleEdit = async (bot) => {
    setSelectedBot(bot);
    setIsViewMode(false);
    await getBotProfileDetail(bot.bot_master_id);
    setIsSettingsOpen(true);
  };

  const handleDelete = async (botId) => {
    if (window.confirm("Are you sure you want to delete this bot profile?")) {
      const success = await deleteBotProfile(botId);
      if (success) {
        setSuccessMessage("Bot profile deleted successfully!");
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 1500);
      } else {
        alert("Failed to delete bot profile.");
      }
    }
  };

  const handleToggleStatus = async (botId) => {
    const success = await toggleBotProfileStatus(botId);
    if (success) {
      setSuccessMessage("Bot status updated successfully!");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 1500);
    } else {
      alert("Failed to toggle bot status.");
    }
  };

  const handleSaveSettings = async (botId, updatedParameters) => {
    const success = await updateBotStrategy(botId, updatedParameters);
    if (success) {
      setSuccessMessage("Bot strategy updated successfully!");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 1500);
      setIsSettingsOpen(false);
    } else {
      alert("Failed to update bot settings.");
    }
  };

  const botCountList = Object.entries(botCounts).map(([name, counts]) => ({
    name,
    ...counts,
  }));

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 text-left">
            Bot Strategy
          </h1>
          <p className="mt-1 text-sm text-gray-500 text-left">
            View, edit, or create new bot strategies.
          </p>
        </div>
        <button
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md transition"
        >
          <PlusIcon className="h-5 w-5" />
          Create Bot
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Bot Name
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="3" className="text-center py-4">
                    Loading Bot Strategies...
                  </td>
                </tr>
              ) : strategies.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center py-4 text-gray-500">
                    No bot strategies found.
                  </td>
                </tr>
              ) : (
                strategies.map((bot) => (
                  <tr key={bot.bot_master_id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 text-left">
                      {bot.bot_master_bot_name}
                    </td>
                    {/* <td className="px-6 py-4 text-left">
                      {bot.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Inactive
                        </span>
                      )}
                    </td> */}
                    <td className="px-6 py-4 text-sm font-medium">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => handleView(bot)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <EyeIcon className="h-5 w-5 mr-1" /> View
                        </button>

                        <button
                          onClick={() => handleEdit(bot)}
                          className="text-indigo-600 hover:text-indigo-900 flex items-center"
                        >
                          <PencilIcon className="h-5 w-5 mr-1" /> Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bot Count Table */}
      <div className="mt-8">
        <h2 className="text-xl font-bold text-gray-900 text-left mb-4">
          Bot Count
        </h2>
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Bot Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Total Bot
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Active Bot
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isCountLoading ? (
                  <tr>
                    <td colSpan="3" className="text-center py-4">
                      Loading Bot Counts...
                    </td>
                  </tr>
                ) : (
                  botCountList.map((bot) => (
                    <tr key={bot.name}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {bot.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {bot.total_bot}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {bot.active_bot}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modals */}
      <BotSettingsModal
        show={isSettingsOpen}
        bot={botDetail}
        isViewMode={isViewMode}
        onClose={() => setIsSettingsOpen(false)}
        onSave={handleSaveSettings}
        onSuccess={(msg) => {
          setSuccessMessage(msg);
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 1500);
        }}
        isSubmitting={isSubmitting}
        isLoading={isDetailLoading}
      />

      <CreateBotModal
        show={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      <SuccessModal
        show={showSuccess}
        message={successMessage}
        onClose={() => setShowSuccess(false)}
      />
    </div>
  );
};

export default BotStrategy;
